import { AppKeys } from "../Components/Shared/AppKeys";

const serverLinkDefault = AppKeys._MEDIA_URL_PRODUCTION;
/**
 * Extracts the host from a full API URL
 * @param {string} url - The full API URL 
 * @returns {string} The host part of the URL including protocol if present 
 */
export const extractHost = (url) => {
  if (!url) return "";

  // Check if the URL contains a protocol
  const hasProtocol = /^https?:\/\//.test(url);

  if (hasProtocol) {
    // Extract protocol and host
    const match = url.match(/^(https?:\/\/[^/]+)/);
    return match ? match[1] : "";
  } else {
    // No protocol, just extract the host part
    const urlWithoutProtocol = url.replace(/^https?:\/\//, "");
    const host = urlWithoutProtocol.split("/")[0];
    return host;
  }
};

/**
 * Extracts the base API URL from a full API URL
 * @param {string} url - The full API URL
 * @returns {string} The base API URL including version
 */
export const extractBaseApiUrl = (url) => {
  if (!url) return "";

  // Check if the URL contains a protocol
  const hasProtocol = /^https?:\/\//.test(url);

  if (hasProtocol) {
    // Extract protocol, host, and API path
    const match = url.match(/^(https?:\/\/[^/]+\/api\/v\d+)/);
    return match ? match[1] : "";
  } else {
    // No protocol, just extract the API path
    const urlWithoutProtocol = url.replace(/^https?:\/\//, "");
    const parts = urlWithoutProtocol.split("/");
    if (parts.length >= 3 && parts[1] === "api" && parts[2].startsWith("v")) {
      return `${parts[0]}/api/${parts[2]}`;
    }
    return "";
  }
};

/**
 * Extracts the endpoint path from a full API URL
 * @param {string} url - The full API URL
 * @returns {string} The endpoint path
 */
export const extractEndpointPath = (url) => {
  if (!url) return "";

  // Remove protocol if present
  const urlWithoutProtocol = url.replace(/^https?:\/\//, "");

  // Split by '/'
  const parts = urlWithoutProtocol.split("/");

  // Find the index of 'api' and 'v1' (or similar version)
  const apiIndex = parts.findIndex((part) => part === "api");
  const versionIndex = apiIndex !== -1 ? apiIndex + 1 : -1;

  if (apiIndex !== -1 && versionIndex !== -1 && versionIndex < parts.length) {
    // Return everything after the version
    return parts.slice(versionIndex + 1).join("/");
  }

  return "";
};

/**
 * Creates a full API URL from a host and endpoint
 * @param {string} host - The host URL 
 * @param {string} endpoint - The API endpoint 
 * @returns {string} The full API URL
 */
export const createApiUrl = (host, endpoint) => {
  if (!host || !endpoint) return "";

  // Remove trailing slash from host if present
  const cleanHost = host.endsWith("/") ? host.slice(0, -1) : host;

  // Remove leading slash from endpoint if present
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;

  return `${cleanHost}/${cleanEndpoint}`;
};

/**
 * Combines the API host and the image path to create a full image URL
 * @param {string} imgPath - The image path 
 * @returns {string} The full image URL
 */
export const getFullFileUrl = (imgPath) => {
  if (!imgPath) return null;

  let host = serverLinkDefault;

  // Clean host (remove trailing slash)
  const cleanHost = host.endsWith("/") ? host.slice(0, -1) : host;

  // Clean path (ensure single forward slashes, remove leading slash)
  let cleanPath = imgPath.replace(/\\/g, "/");
  if (cleanPath.startsWith("/")) {
    cleanPath = cleanPath.slice(1);
  }

  return `${cleanHost}/${cleanPath}`;
};

/**
 * Converts a physical file path (e.g., from server's wwwroot) to a public URL
 * @param {string} physicalPath - The full physical path returned by the backend
 * @param {string} baseMediaUrl - The base URL of the media server
 * @returns {string} The formatted public URL
 */
export const getPublicUrlFromPhysicalPath = (physicalPath, baseMediaUrl = serverLinkDefault) => {
  if (!physicalPath) return "";

  let relativePath = physicalPath;
  if (relativePath.includes("wwwroot")) {
    const parts = relativePath.split(/wwwroot[\\/]/);
    relativePath = parts[parts.length - 1].replace(/\\/g, "/");
  } else {
    relativePath = relativePath.replace(/\\/g, "/");
  }

  // Clean base media url (remove trailing slash)
  const cleanHost = baseMediaUrl.endsWith("/") ? baseMediaUrl.slice(0, -1) : baseMediaUrl;

  // Clean path (remove leading slash)
  if (relativePath.startsWith("/")) {
    relativePath = relativePath.slice(1);
  }

  return `${cleanHost}/${relativePath}`;
};
